import {useCallback, useState} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

export const validateWarrantyConsistency = (basket) => {
    const errors = []
    const itemsToRemove = []
    const items = basket?.productItems || []

    // Return early if basket is empty
    if (!items.length) {
        return {ok: true, errors, itemsToRemove: []}
    }

    // TODO: remove this check at phase 2
    // Check for warranties without UUIDWarranty
    items.forEach((item) => {
        if (item.c_isWarranty && !item.c_UUIDWarranty) {
            const errorMsg = `Warranty item ${item.productId} is missing required UUIDWarranty`
            errors.push(errorMsg)
            itemsToRemove.push({productId: item.productId, uuid: null})
        }
    })

    // Group items by UUID warranty
    const itemsByUUID = {}
    items.forEach((item) => {
        const uuid = item.c_UUIDWarranty
        if (uuid) {
            if (!itemsByUUID[uuid]) {
                itemsByUUID[uuid] = []
            }
            itemsByUUID[uuid].push(item)
        }
    })

    // Validate each UUID group
    Object.entries(itemsByUUID).forEach(([uuid, uuidItems]) => {
        // Rule 1: each UUID must have exactly 2 items (1 product + 1 warranty)
        if (uuidItems.length !== 2) {
            const errorMsg = `UUID ${uuid} must have exactly one product and one warranty`
            errors.push(errorMsg)
            uuidItems.forEach((item) => {
                itemsToRemove.push({productId: item.productId, uuid})
            })
            return
        }

        // Rule 2: one item must be a product, one must be a warranty
        const hasProduct = uuidItems.some((item) => !item.c_isWarranty)
        const hasWarranty = uuidItems.some((item) => item.c_isWarranty)

        if (!hasProduct || !hasWarranty) {
            const errorMsg = `UUID ${uuid} must have both a product and a warranty`
            errors.push(errorMsg)
            uuidItems.forEach((item) => {
                itemsToRemove.push({productId: item.productId, uuid})
            })
            return
        }

        // Rule 3: quantity of both product and warranty must be 1
        uuidItems.forEach((item) => {
            if (item.quantity !== 1) {
                const itemType = item.c_isWarranty ? 'warranty' : 'product'
                const errorMsg = `${itemType} quantity must be 1 (UUID ${uuid})`
                errors.push(errorMsg)
                itemsToRemove.push({productId: item.productId, uuid})
            }
        })
    })

    return {
        ok: errors.length === 0,
        errors,
        itemsToRemove,
    }
}

export const useValidateWarrantyBasket = ({basket, onValidationFail, redirectAfterClear} = {}) => {
    const navigate = useNavigation()
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const [isClearingBasket, setIsClearingBasket] = useState(false)

    const validateWarrantyBeforeAction = useCallback(() => {
        const {ok, errors} = validateWarrantyConsistency(basket)

        if (!ok) {
            console.error('Warranty consistency validation failed:', errors)
            onValidationFail?.()
            return false
        }

        return true
    }, [basket, onValidationFail])

    const clearBasket = useCallback(async () => {
        if (!basket?.basketId) return

        setIsClearingBasket(true)

        try {
            const productItems = basket?.productItems || []

            await Promise.all(
                productItems.map((item) =>
                    removeItemFromBasketMutation
                        .mutateAsync({
                            parameters: {
                                basketId: basket.basketId,
                                itemId: item.itemId,
                            },
                        })
                        .catch((error) => {
                            console.warn('Error removing basket item:', item.itemId, error)
                        }),
                ),
            )

            if (redirectAfterClear) {
                navigate(redirectAfterClear)
            }
        } finally {
            setIsClearingBasket(false)
        }
    }, [basket, removeItemFromBasketMutation, redirectAfterClear, navigate])

    return {
        validateWarrantyBeforeAction,
        clearBasket,
        isClearingBasket,
    }
}
